import { useState, useEffect } from 'react';
import { getAppearance } from '../../../services/appearance.service';
import { updateUserProfile } from '../../../services/profile.service';
import { AppearanceInterface } from '../../../types';
import Avatar, { genConfig } from 'funifier-nice-avatar';
import AvatarDraft from './AvatarDraft';
import { useAuth } from 'contexts';
import { IoClose } from 'react-icons/io5';
import { BiDice5, BiSave } from 'react-icons/bi';
import { MdOutlineCancel } from 'react-icons/md';

interface AvatarModalEditorProps {
  isOpen: boolean;
  onClose: () => void;
  userAvatarObject: object | null;
}

const randomizeAvatar = () => ({
  sex: ['man', 'woman'][Math.floor(Math.random() * 2)],
  faceColor: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'),
  earSize: ['small', 'big'][Math.floor(Math.random() * 2)],
  hairColor: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'),
  hairStyle: ['normal', 'thick', 'mohawk', 'womanLong', 'womanShort'][Math.floor(Math.random() * 5)],
  hairColorRandom: false,
  hatColor: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'),
  hatStyle: ['none', 'beanie', 'turban'][Math.floor(Math.random() * 3)],
  eyeStyle: ['circle', 'oval', 'smile'][Math.floor(Math.random() * 3)],
  glassesStyle: ['none', 'round', 'square'][Math.floor(Math.random() * 3)],
  noseStyle: ['short', 'long', 'round'][Math.floor(Math.random() * 3)],
  mouthStyle: ['laugh', 'smile', 'peace'][Math.floor(Math.random() * 3)],
  shirtStyle: ['hoody', 'short', 'polo'][Math.floor(Math.random() * 3)],
  shirtColor: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'),
  bgColor: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'),
  isGradient: false,
});

export default function AvatarModalEditor({ isOpen, onClose, userAvatarObject }: AvatarModalEditorProps) {
  const { user } = useAuth();

  const [colors, setColors] = useState({
    primaryColor: '',
    secondaryColor: '',
  });

  const [draftAvatar, setDraftAvatar] = useState<any>(userAvatarObject);
  const config = draftAvatar ? genConfig(draftAvatar) : genConfig();

  useEffect(() => {
    const fetchAppearance = async () => {
      const data = await getAppearance();
      setDraftAvatar(userAvatarObject);
      setColors({
        primaryColor: data?.layout?.bgPage || '#ff5400',
        secondaryColor: data?.layout?.colorMenuIdle || '#000000',
      });
    };
    fetchAppearance();
  }, [userAvatarObject]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    if (isOpen) {
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollBarWidth > 0) {
        document.body.style.paddingRight = `${scrollBarWidth}px`;
      }
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isOpen]);

  const closeModal = () => {
    setDraftAvatar(userAvatarObject);
    onClose()
  }

  const handleSubmit = async () => {
    try {
      const payload = {
        _id: user?._id,
        extra: { avatar: draftAvatar },
      };
      await updateUserProfile(payload);
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error updating user profile:', error);
    }
  };

  const handleRandomize = () => {
    setDraftAvatar(randomizeAvatar());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-2 md:p-4">
      <div
        className="w-full max-w-4xl max-h-[95vh] md:max-h-[75vh] rounded-xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
        style={{ backgroundColor: '#f5f5f5' }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 md:px-6 md:py-4"
          style={{ backgroundColor: colors.primaryColor }}
        >
          <h2 className="text-base md:text-lg font-bold text-white tracking-wide">
            Personalize seu Avatar
          </h2>
          <button
            onClick={closeModal}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-all text-white"
          >
            <IoClose size={20} />
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          <div
            className="md:w-[35%] flex flex-col items-center justify-center gap-3 p-3 md:gap-4 md:p-4 md:border-r md:border-gray-200"
            style={{ backgroundColor: '#ebebeb' }}
          >
            <div
              className="rounded-full p-1 shadow-xl"
              style={{ border: `4px solid ${colors.primaryColor}` }}
            >
              <Avatar style={{ width: 180, height: 180 }} className="sm:w-[20px] sm:h-[20px]" {...config} />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 md:p-5">
            <AvatarDraft
              userAvatar={draftAvatar}
              setUserAvatar={setDraftAvatar}
              primaryColor={colors.primaryColor}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between px-4 py-3 md:px-6 md:py-4 border-t border-gray-200 bg-white gap-2 md:gap-0">
          <button
            onClick={onClose}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full border-2 border-gray-300 text-gray-600 font-semibold text-sm hover:bg-gray-100 transition-all uppercase tracking-wider"
          >
            <MdOutlineCancel size={18} />
            Cancelar
          </button>

          <div className="flex flex-row items-center gap-2 md:gap-3 w-full md:w-auto">
            <button
              onClick={handleRandomize}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full font-semibold text-sm text-white transition-all uppercase tracking-wider hover:brightness-110"
              style={{ backgroundColor: colors.primaryColor }}
            >
              <BiDice5 size={18} />
              <span className="hidden md:inline">Aleatório</span>
              <span className="md:hidden">Sortear</span>
            </button>

            <button
              onClick={handleSubmit}
              className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full font-semibold text-sm text-white transition-all uppercase tracking-wider hover:brightness-110"
              style={{ backgroundColor: colors.secondaryColor || '#222' }}
            >
              <BiSave size={18} />
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
